import React, { useState } from "react";
import { useLoader } from "../../../utils/LoaderUtils";
import { showError, showSuccess } from "../../../utils/toastUtils";
import useNumericInput from "../../../hooks/useNumericInput";
import StoreAPI from "../../../api/StoreApi";
import ImageUploader from "../../../components/partials/ImageUploader";
import { handleApiValidationError } from "../../../utils/apiErrorHandler";
import Auth from "../../../utils/AuthUtils";

function GeneralSettings() {
  useNumericInput();

  const { showLoader, hideLoader } = useLoader();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const roles = Auth.getUserDetails("roles");

  const INTIAL_FORM_DATA = {
    name: "",
    domain: "",
    websiteUrl: "",
    adminPanelUrl: "",
    phone: "",
    alternatePhone: "",
    email: "",
    addressLine: "",
    shortDescription: "",
    pincode: "",
    state: "",
    district: "",
    mapLocation: "",
    gst: "",
    productVisibility: "cart",
    status: "active",
    logo: "",
    favicon: "",
    whatsappNumber: "",
    shippingAmount: "",
    shippingCost: "",
    receiverEmail: "",
  };

  const [formData, setFormData] = useState(INTIAL_FORM_DATA);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "pincode") {
      handlePincodeChange(value);
    }
  };

  const handlePincodeChange = async (value) => {
    setFormData((prev) => ({ ...prev, pincode: value }));

    if (value.length === 6) {
      const res = await fetch(`https://api.postalpincode.in/pincode/${value}`);
      const data = await res.json();

      if (data[0].Status === "Success") {
        setFormData((prev) => ({
          ...prev,
          state: data[0].PostOffice[0].State,
          district: data[0].PostOffice[0].District,
        }));
      } else {
        showError("Invalid Pincode");
      }
    }
  };

  const handleImageUploadSuccess = (type, url) => {
    setFormData((prev) => ({
      ...prev,
      [type]: url,
    }));
  };

  const validate = () => {
    const phoneRegex = /^[6-9]\d{9}$/;
    const gstRegex =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

    const newErrors = {};

    if (!formData.name) {
      newErrors.name = "Store Name is required";
    }
    if (!formData.email) {
      newErrors.email = "Email is required";
    }

    if (!formData.domain) {
      newErrors.domain = "Domain is required";
    }

    if (!formData.phone) {
      newErrors.phone = "Phone Number is required";
    } else if (formData.phone && !phoneRegex.test(formData.phone)) {
      newErrors.phone = "Invalid Phone Number";
    }

    if (!formData.pincode) {
      newErrors.pincode = "PinCode is required";
    } else if (formData.pincode.length !== 6) {
      newErrors.pincode = "PinCode must be of 6 characters";
    }

    if (!formData.state) {
      newErrors.state = "State is required";
    }

    if (!formData.district) {
      newErrors.district = "District is required";
    }

    if (!formData.addressLine) {
      newErrors.addressLine = "Address is required";
    }

    if (!formData.pincode) {
      newErrors.pincode = "Invalid PinCode";
    }

    if (formData.gst) {
      if (formData.gst.length !== 15) {
        newErrors.gst = "GSTIN must contains 15 characters.";
      } else if (!gstRegex.test(formData.gst)) {
        newErrors.gst = "Invalid GSTIN format (e.g. 22AAAAA0000A1Z5).";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    showLoader();
    setIsLoading(true);

    e.preventDefault();

    if (!validate()) {
      setIsLoading(false);
      hideLoader();
      return;
    }

    try {
      const res = await StoreAPI.addNewStore(formData);

      if (res.code === 200) {
        showSuccess(res.message);

        setFormData(INTIAL_FORM_DATA);
      } else {
        showError(res.message);
      }

      setIsLoading(false);
      hideLoader();
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  return (
    <>
      <div className="card-header d-flex align-items-center justify-content-between">
        <h4>General Settings</h4>
      </div>
      <div className="card-body">
        <form method="post" onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6">
              <ImageUploader
                type="logo"
                label="Logo"
                value={formData.logo}
                onUploadSuccess={handleImageUploadSuccess}
                assetType="stores"
              />
            </div>
            <div className="col-md-6">
              <ImageUploader
                type="favicon"
                label="favicon"
                value={formData.favicon}
                onUploadSuccess={handleImageUploadSuccess}
                assetType="stores"
              />
            </div>
          </div>

          <div className="row">
            <div className="col-lg-6 col-sm-12">
              <div className="mb-3">
                <label className="form-label">
                  Store Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                />
                {errors.name && (
                  <span className="errors text-danger">{errors.name}</span>
                )}
              </div>
            </div>

            {roles === "SUPERADMIN" && (
              <>
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">Domain</label>
                    <input
                      type="text"
                      className="form-control"
                      name="domain"
                      value={formData.domain}
                      onChange={handleChange}
                    />
                    {errors.domain && (
                      <span className="errors text-danger">
                        {errors.domain}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">Website Url</label>
                    <input
                      type="text"
                      className="form-control"
                      name="websiteUrl"
                      value={formData.websiteUrl}
                      onChange={handleChange}
                    />
                    {errors.websiteUrl && (
                      <span className="errors text-danger">
                        {errors.websiteUrl}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-lg-6 col-sm-6">
                  <div className="mb-3">
                    <label className="form-label">Admin Panel Url</label>
                    <input
                      type="text"
                      className="form-control"
                      name="adminPanelUrl"
                      value={formData.adminPanelUrl}
                      onChange={handleChange}
                    />
                    {errors.adminPanelUrl && (
                      <span className="errors text-danger">
                        {errors.adminPanelUrl}
                      </span>
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="col-lg-12 col-sm-12">
              <div className="mb-3">
                <label className="form-label">Short Description</label>
                <textarea
                  className="form-control"
                  name="shortDescription"
                  value={formData.shortDescription}
                  onChange={handleChange}
                ></textarea>
                {errors.shortDescription && (
                  <span className="errors text-danger">
                    {errors.shortDescription}
                  </span>
                )}
              </div>
            </div>
            <div className="col-lg-4 col-sm-12">
              <div className="mb-3">
                <label className="form-label">
                  Contact Email Address<span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  value={formData.email}
                  onChange={handleChange}
                />
                {errors.email && (
                  <span className="errors text-danger">{errors.email}</span>
                )}
              </div>
            </div>
            <div className="col-lg-4 col-sm-12">
              <div className="mb-3">
                <label className="form-label">
                  Contact Phone Number<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control only-numeric"
                  maxLength={10}
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
                {errors.phone && (
                  <span className="errors text-danger">{errors.phone}</span>
                )}
              </div>
            </div>
            <div className="col-lg-4 col-sm-12">
              <div className="mb-3">
                <label className="form-label">
                  Alternate Phone Number<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control only-numeric"
                  maxLength={10}
                  name="alternatePhone"
                  value={formData.alternatePhone}
                  onChange={handleChange}
                />
                {errors.alternatePhone && (
                  <span className="errors text-danger">
                    {errors.alternatePhone}
                  </span>
                )}
              </div>
            </div>
            <div className="col-lg-4 col-sm-4">
              <div className="mb-3">
                <label className="form-label">
                  PinCode<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control only-numeric"
                  maxLength={6}
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                />
                {errors.pincode && (
                  <span className="errors text-danger">{errors.pincode}</span>
                )}
              </div>
            </div>
            <div className="col-lg-4 col-sm-4">
              <div className="mb-3">
                <label className="form-label">
                  State<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                />
                {errors.state && (
                  <span className="errors text-danger">{errors.state}</span>
                )}
              </div>
            </div>
            <div className="col-lg-4 col-sm-4">
              <div className="mb-3">
                <label className="form-label">
                  District<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                />
                {errors.district && (
                  <span className="errors text-danger">{errors.district}</span>
                )}
              </div>
            </div>
            <div className="col-lg-12 col-sm-12">
              <div className="mb-3">
                <label className="form-label">
                  Address<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="addressLine"
                  value={formData.addressLine}
                  onChange={handleChange}
                />
                {errors.addressLine && (
                  <span className="errors text-danger">
                    {errors.addressLine}
                  </span>
                )}
              </div>
            </div>
            <div className="col-lg-12 col-sm-12">
              <div className="mb-3">
                <label className="form-label">Map Location</label>
                <input
                  type="text"
                  className="form-control"
                  name="mapLocation"
                  placeholder="Map Location Url"
                  value={formData.mapLocation}
                  onChange={handleChange}
                />
                {errors.mapLocation && (
                  <span className="errors text-danger">
                    {errors.mapLocation}
                  </span>
                )}
              </div>
            </div>

            <div className="col-lg-6 col-sm-6">
              <div className="mb-3">
                <label className="form-label">GSTIN</label>
                <input
                  type="text"
                  className="form-control"
                  name="gst"
                  placeholder="e.g. 22AAAAA0000A1Z5"
                  value={formData.gst || ""}
                  onChange={handleChange}
                  maxLength={15}
                />
                {errors.gst && (
                  <span className="errors text-danger">{errors.gst}</span>
                )}
              </div>
            </div>

            <div className="col-lg-6 col-sm-6">
              <div className="mb-3">
                <label className="form-label">
                  Show Product In <span className="text-danger">*</span>
                </label>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="productVisibility"
                    id="addToCartOption"
                    value="cart"
                    checked={formData.productVisibility === "cart"}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="addToCartOption">
                    Add to Cart
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="productVisibility"
                    id="catalogueOption"
                    value="catalogue"
                    checked={formData.productVisibility === "catalogue"}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="catalogueOption">
                    Catalogue Only (No Cart Option)
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-md-12">
              <h6>Other Information</h6>
            </div>
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Whatsapp Number</label>
                <input
                  type="text"
                  name="whatsappNumber"
                  className="form-control only-numeric"
                  placeholder="Whatsapp Number"
                  maxLength={10}
                  value={formData.whatsappNumber}
                  onChange={handleChange}
                />
                {errors.whatsappNumber && (
                  <span className="errors text-danger">
                    {errors.whatsappNumber}
                  </span>
                )}
              </div>
            </div>
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Free Shipping Above (₹)</label>
                <input
                  type="text"
                  name="shippingAmount"
                  className="form-control only-numeric"
                  placeholder="Enter amount (e.g. 999)"
                  value={formData.shippingAmount}
                  onChange={handleChange}
                  min="0"
                />
                {errors.shippingAmount && (
                  <span className="errors text-danger">
                    {errors.shippingAmount}
                  </span>
                )}
              </div>
            </div>
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Shipping Cost (₹)</label>
                <input
                  type="text"
                  name="shippingCost"
                  className="form-control only-numeric"
                  placeholder="Enter shipping cost (e.g. 50)"
                  value={formData.shippingCost}
                  onChange={handleChange}
                  min="0"
                />
                {errors.shippingCost && (
                  <span className="errors text-danger">
                    {errors.shippingCost}
                  </span>
                )}
              </div>
            </div>

            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Email (to receive mails)</label>
                <input
                  type="email"
                  name="receiverEmail"
                  className="form-control"
                  placeholder="Email Address"
                  value={formData.receiverEmail}
                  onChange={handleChange}
                />
                {errors.receiverEmail && (
                  <span className="errors text-danger">
                    {errors.receiverEmail}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12 d-flex justify-content-end">
              {!isLoading && (
                <button
                  type="submit"
                  id="submitBtn"
                  className="btn btn-primary shadow-none"
                >
                  Submit
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

export default GeneralSettings;
